import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
  WorkOrdersByStatus,
  WorkOrderStatusOrder,
} from "../models/workOrderTypes";
import localStorageHelper from "../../../app/localStorage";
import WorkOrderQueueAPI from "../api/workOrderApi";
import { convertToRGBA } from "../../common/Utils/commonUtilityFunctions";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { dedupeColumnsByIdentity, toColumnArray } from "../shared/boardColumnUtils";

import packageJson from "../../../../package.json";

const STATUS_CONFIG_DROPPABLE_ID = "status-config-columns";

const normalizeStatusColumns = (
  columns: WorkOrdersByStatus[] | Record<string, WorkOrdersByStatus> | null | undefined,
): WorkOrdersByStatus[] =>
  dedupeColumnsByIdentity(toColumnArray(columns)).sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

interface StatusBoardCustomColumnsProps {
  columns: WorkOrdersByStatus[] | Record<string, WorkOrdersByStatus> | null | undefined;
  onSave: (updatedColumns: WorkOrdersByStatus[]) => void;
  onCancel: () => void;
}

const StatusBoardCustomColumn: React.FC<StatusBoardCustomColumnsProps> = ({
  columns,
  onSave,
  onCancel,
}) => {
  const user = localStorageHelper.getByKey("user");
  const [orderedColumns, setOrderedColumns] = React.useState<
    WorkOrdersByStatus[]
  >(() => normalizeStatusColumns(columns));

  React.useEffect(() => {
    setOrderedColumns(normalizeStatusColumns(columns));
  }, [columns]);

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedColumns = items.map((column, index) => ({
      ...column,
      displayOrder: index + 1,
    }));

    setOrderedColumns(updatedColumns);

    const payload: WorkOrderStatusOrder[] = dedupeColumnsByIdentity(
      updatedColumns,
    ).map((column) => ({
      statusIdentity: column.identity,
      displayOrder: column.displayOrder,
      modifiedByUserIdentity: user.identity,
      modifiedDate: new Date().toISOString(),
    }));

    try {
      await WorkOrderQueueAPI.saveWorkOrderStatusOrder(payload);
    } catch {
      setOrderedColumns(normalizeStatusColumns(columns));
      return;
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "onDragEnd",
      {
        elapsed_ms: "124",
        title: "status Board Column Customization Component",
        componentName: "StatusBoardCustomColumns.tsx",
        version: packageJson.version,
      },
    );
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Typography variant="h5" gutterBottom>
        Configure Column Order
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Drag and drop columns to reorder them
      </Typography>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={STATUS_CONFIG_DROPPABLE_ID}
          direction="horizontal"
        >
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                position: "relative",
                my: 2,
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2,
                overflowX: "auto",
                overflowY: "visible",
                minHeight: 280,
                py: 1,
              }}
            >
              {orderedColumns.map((column, index) => (
                <Draggable
                  key={column.identity}
                  draggableId={`status-col-${column.identity}`}
                  index={index}
                >
                  {(dragProvided) => (
                    <Paper
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      elevation={2}
                      sx={{
                        p: 2,
                        minHeight: 250,
                        minWidth: 250,
                        flexShrink: 0,
                        display: "flex",
                        bgcolor: "lightgray",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        cursor: "grab",
                        "&:active": {
                          cursor: "grabbing",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontStyle: "normal",
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#121217",
                          mb: 1,
                        }}
                      >
                        <Chip
                          label=""
                          size="small"
                          sx={{
                            bgcolor: convertToRGBA(column.statusColor),
                            color: `${column.textColor}`,
                            mb: "3px",
                            mr: "5px",
                            width: "12px",
                            height: "12px",
                          }}
                        />
                        {column.displayText}
                      </Typography>
                      <Typography variant="caption">
                        Order: {column.displayOrder ?? index + 1}
                      </Typography>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button sx={{ textTransform: "capitalize" }} variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button sx={{ textTransform: "capitalize" }} variant="contained" onClick={() => onSave(orderedColumns)}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default StatusBoardCustomColumn;
